import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Grid,
  IconButton,
  Chip,
  RadioGroup,
  Radio,
  FormLabel,
  Paper,
  Divider
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import InstagramIcon from "@material-ui/icons/Instagram";
import FacebookIcon from "@material-ui/icons/Facebook";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(3),
    minWidth: 500,
    maxWidth: 700
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  cardSection: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default
  },
  replyItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  channelBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6
  }
}));

const MetaCommentAutomationModal = ({
  open,
  onClose,
  automationId,
  onSave
}) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [flows, setFlows] = useState([]);

  // Form State
  const [name, setName] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [channel, setChannel] = useState("instagram");
  const [postScope, setPostScope] = useState("all"); // 'all' | 'specific'
  const [postId, setPostId] = useState("");
  const [conditionType, setConditionType] = useState("any"); // 'any' | 'contains' | 'exact'
  const [keywords, setKeywords] = useState("");
  
  // Public Reply State
  const [publicReplyEnabled, setPublicReplyEnabled] = useState(false);
  const [publicReplies, setPublicReplies] = useState(["¡Hola {name}! Te hemos enviado un mensaje privado con la información 🚀"]);

  // Private Reply State
  const [privateReplyEnabled, setPrivateReplyEnabled] = useState(true);
  const [privateReplyMessage, setPrivateReplyMessage] = useState("¡Hola {name}! Gracias por tu interés. Aquí tienes la información solicitada 👇");
  const [flowId, setFlowId] = useState("");
  const [isActive, setIsActive] = useState(true);

  // Cargar conexiones y flujos
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [whatsappsRes, flowsRes] = await Promise.all([
          api.get("/whatsapp"),
          api.get("/flowbuilder")
        ]);

        // Filtrar solo conexiones de Facebook e Instagram
        const metaConnections = (whatsappsRes.data || []).filter(
          (w) => w.channel === "instagram" || w.channel === "facebook"
        );
        setConnections(metaConnections);

        // Flujos disponibles
        setFlows(flowsRes.data?.flows || flowsRes.data || []);
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchMetadata();
    }
  }, [open]);

  // Cargar automatización si es edición
  useEffect(() => {
    if (open && automationId) {
      const fetchAutomation = async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/meta-comments`);
          const current = (data || []).find((a) => a.id === automationId);
          if (current) {
            setName(current.name || "");
            setWhatsappId(current.whatsappId || "");
            setChannel(current.channel || "instagram");
            setPostScope(current.postId ? "specific" : "all");
            setPostId(current.postId || "");
            setConditionType(current.conditionType || "any");
            setKeywords(current.keywords || "");
            setPublicReplyEnabled(!!current.publicReplyEnabled);
            setPublicReplies(
              Array.isArray(current.publicReplies) && current.publicReplies.length > 0
                ? current.publicReplies
                : ["¡Hola {name}! Te hemos enviado un mensaje privado 🚀"]
            );
            setPrivateReplyEnabled(!!current.privateReplyEnabled);
            setPrivateReplyMessage(current.privateReplyMessage || "");
            setFlowId(current.flowId || "");
            setIsActive(current.isActive !== false);
          }
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };
      fetchAutomation();
    } else if (open && !automationId) {
      // Reset defaults
      setName("");
      setWhatsappId("");
      setChannel("instagram");
      setPostScope("all");
      setPostId("");
      setConditionType("any");
      setKeywords("");
      setPublicReplyEnabled(false);
      setPublicReplies(["¡Hola {name}! Te hemos enviado un mensaje privado con los detalles 🚀"]);
      setPrivateReplyEnabled(true);
      setPrivateReplyMessage("¡Hola {name}! Gracias por comentar. ¿En qué podemos ayudarte hoy?");
      setFlowId("");
      setIsActive(true);
    }
  }, [open, automationId]);

  // Sincronizar canal al cambiar conexión
  const handleWhatsappChange = (e) => {
    const selectedId = e.target.value;
    setWhatsappId(selectedId);
    const conn = connections.find((c) => c.id === selectedId);
    if (conn) {
      setChannel(conn.channel);
    }
  };

  const handleAddPublicReply = () => {
    setPublicReplies([...publicReplies, ""]);
  };

  const handlePublicReplyChange = (index, value) => {
    const updated = [...publicReplies];
    updated[index] = value;
    setPublicReplies(updated);
  };

  const handleRemovePublicReply = (index) => {
    if (publicReplies.length <= 1) {
      toast.warning(i18n.t("metaComments.validation.atLeastOneReply"));
      return;
    }
    const updated = publicReplies.filter((_, i) => i !== index);
    setPublicReplies(updated);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error(i18n.t("metaComments.validation.nameRequired"));
      return;
    }
    if (!whatsappId) {
      toast.error(i18n.t("metaComments.validation.connectionRequired"));
      return;
    }
    if (postScope === "specific" && !postId.trim()) {
      toast.error(i18n.t("metaComments.validation.postIdRequired"));
      return;
    }
    if (conditionType !== "any" && !keywords.trim()) {
      toast.error(i18n.t("metaComments.validation.keywordsRequired"));
      return;
    }
    if (publicReplyEnabled && publicReplies.filter(r => r.trim()).length === 0) {
      toast.error(i18n.t("metaComments.validation.publicReplyRequired"));
      return;
    }
    if (privateReplyEnabled && !privateReplyMessage.trim()) {
      toast.error(i18n.t("metaComments.validation.privateReplyRequired"));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name,
        whatsappId: Number(whatsappId),
        channel,
        postId: postScope === "specific" ? postId.trim() : null,
        conditionType,
        keywords: conditionType !== "any" ? keywords.trim() : null,
        publicReplyEnabled,
        publicReplies: publicReplyEnabled ? publicReplies.filter(r => r.trim()) : [],
        privateReplyEnabled,
        privateReplyMessage: privateReplyEnabled ? privateReplyMessage.trim() : null,
        flowId: flowId ? Number(flowId) : null,
        isActive
      };

      if (automationId) {
        await api.put(`/meta-comments/${automationId}`, payload);
        toast.success(i18n.t("metaComments.messages.updatedSuccess"));
      } else {
        await api.post("/meta-comments", payload);
        toast.success(i18n.t("metaComments.messages.createdSuccess"));
      }

      if (onSave) onSave();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {automationId
          ? i18n.t("metaComments.modal.editTitle")
          : i18n.t("metaComments.modal.createTitle")}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {/* 1. Datos Generales */}
        <Box className={classes.cardSection}>
          <Typography className={classes.sectionTitle}>
            {i18n.t("metaComments.modal.generalInfo")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label={i18n.t("metaComments.modal.ruleName")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Ej: Lanzamiento Ebook - Comentarios"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label={isActive ? i18n.t("metaComments.modal.active") : i18n.t("metaComments.modal.inactive")}
                style={{ marginTop: 4 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{i18n.t("metaComments.modal.selectConnection")}</InputLabel>
                <Select
                  value={whatsappId}
                  onChange={handleWhatsappChange}
                  label={i18n.t("metaComments.modal.selectConnection")}
                >
                  {connections.map((conn) => (
                    <MenuItem key={conn.id} value={conn.id}>
                      <span className={classes.channelBadge}>
                        {conn.channel === "instagram" ? (
                          <InstagramIcon fontSize="small" style={{ color: "#E1306C" }} />
                        ) : (
                          <FacebookIcon fontSize="small" style={{ color: "#1877F2" }} />
                        )}
                        {conn.name} ({conn.channel})
                      </span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend" style={{ fontSize: "0.8rem" }}>
                  {i18n.t("metaComments.modal.postScope")}
                </FormLabel>
                <RadioGroup
                  row
                  value={postScope}
                  onChange={(e) => setPostScope(e.target.value)}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" color="primary" />}
                    label={i18n.t("metaComments.modal.allPosts")}
                  />
                  <FormControlLabel
                    value="specific"
                    control={<Radio size="small" color="primary" />}
                    label={i18n.t("metaComments.modal.specificPost")}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {postScope === "specific" && (
              <Grid item xs={12}>
                <TextField
                  label={i18n.t("metaComments.modal.postIdLabel")}
                  value={postId}
                  onChange={(e) => setPostId(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="ID del post o URL de la publicación"
                  helperText={i18n.t("metaComments.modal.postIdHelper")}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        {/* 2. Disparador de Palabras Clave */}
        <Box className={classes.cardSection}>
          <Typography className={classes.sectionTitle}>
            {i18n.t("metaComments.modal.triggerSection")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{i18n.t("metaComments.modal.conditionType")}</InputLabel>
                <Select
                  value={conditionType}
                  onChange={(e) => setConditionType(e.target.value)}
                  label={i18n.t("metaComments.modal.conditionType")}
                >
                  <MenuItem value="any">{i18n.t("metaComments.conditions.any")}</MenuItem>
                  <MenuItem value="contains">{i18n.t("metaComments.conditions.contains")}</MenuItem>
                  <MenuItem value="exact">{i18n.t("metaComments.conditions.exact")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {conditionType !== "any" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={i18n.t("metaComments.modal.keywordsLabel")}
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="precio, info, quiero, catálogo"
                  helperText={i18n.t("metaComments.modal.keywordsHelper")}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        {/* 3. Respuesta Pública en la Publicación */}
        <Box className={classes.cardSection}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography className={classes.sectionTitle}>
              💬 {i18n.t("metaComments.modal.publicReplyTitle")}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={publicReplyEnabled}
                  onChange={(e) => setPublicReplyEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={publicReplyEnabled ? i18n.t("metaComments.modal.enabled") : i18n.t("metaComments.modal.disabled")}
            />
          </Box>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
            {i18n.t("metaComments.modal.publicReplyDescription")}
          </Typography>

          {publicReplyEnabled && (
            <Box>
              {publicReplies.map((reply, idx) => (
                <Box key={idx} className={classes.replyItem}>
                  <TextField
                    value={reply}
                    onChange={(e) => handlePublicReplyChange(idx, e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="¡Hola {name}! Te enviamos un DM con la info 📩"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePublicReply(idx)}
                    color="secondary"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                color="primary"
                onClick={handleAddPublicReply}
                style={{ marginTop: 4 }}
              >
                {i18n.t("metaComments.modal.addReplyVariant")}
              </Button>
            </Box>
          )}
        </Box>

        {/* 4. Respuesta Privada (Direct Message / DM) */}
        <Box className={classes.cardSection}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography className={classes.sectionTitle}>
              ✉️ {i18n.t("metaComments.modal.privateReplyTitle")}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={privateReplyEnabled}
                  onChange={(e) => setPrivateReplyEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={privateReplyEnabled ? i18n.t("metaComments.modal.enabled") : i18n.t("metaComments.modal.disabled")}
            />
          </Box>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
            {i18n.t("metaComments.modal.privateReplyDescription")}
          </Typography>

          {privateReplyEnabled && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={i18n.t("metaComments.modal.privateMessageText")}
                  value={privateReplyMessage}
                  onChange={(e) => setPrivateReplyMessage(e.target.value)}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="¡Hola {name}! Gracias por comentar. Aquí tienes el enlace..."
                  helperText={i18n.t("metaComments.modal.variableHint")}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>{i18n.t("metaComments.modal.assignFlow")}</InputLabel>
                  <Select
                    value={flowId}
                    onChange={(e) => setFlowId(e.target.value)}
                    label={i18n.t("metaComments.modal.assignFlow")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("metaComments.modal.noFlow")}</em>
                    </MenuItem>
                    {flows.map((fl) => (
                      <MenuItem key={fl.id} value={fl.id}>
                        {fl.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                  {i18n.t("metaComments.modal.flowHelper")}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default" disabled={loading}>
          {i18n.t("common.cancel") || "Cancelar"}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading
            ? i18n.t("common.saving") || "Guardando..."
            : i18n.t("common.save") || "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetaCommentAutomationModal;
